import { useCallback } from 'react'
import { ErrorBoundary } from 'common/components/ErrorBoundary/ErrorBoundary'
import { useNavigate } from 'react-router-dom'
import { Button, useNotifications } from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useCurrentUser } from 'common/hooks'
import PageError from 'components/ErrorPages/PageError'
import { InboxPreview, NodeTimeline } from 'Automations/components'
import { ManualTask } from '../../manualTasks/types'
import { notificationDefinitions } from '../notificationDefinitions'

export const NotificationPreview = ({
  task,
  dismissNavigateTo
}: {
  task: ManualTask
  dismissNavigateTo: string
}) => {
  const notificationId = task.id
  const { displayNotification } = useNotifications()
  const navigate = useNavigate()
  const currentUser = useCurrentUser()

  const completeTaskMutation = useMutation(
    dashboardv99990101Client.postCompleteManualTask,
    {
      onSuccess: () => {
        displayNotification('Notification dismissed', {
          type: 'success'
        })
        navigate(dismissNavigateTo)
      },
      onError: () => {
        displayNotification('Failed to dismiss notification', {
          type: 'error'
        })
      }
    }
  )

  const handleDone = useCallback(async () => {
    await completeTaskMutation.mutateAsync({
      id: notificationId,
      outputData: {
        completedBy: currentUser.id
      }
    })
  }, [notificationId, currentUser.id, completeTaskMutation])

  const definition = notificationDefinitions[task.type]

  if (!definition) {
    return <InboxPreview.EmptyState />
  }

  const isTaskPending = task.status === 'PENDING'

  const PreviewContent = definition.previewContent
  const PreviewHeaderContent = definition.previewHeaderContent
  const dismissAction = definition.dismissAction
  const PreviewHeaderActions = definition.previewHeaderActions

  // The notification's own action (e.g. "View in Xero") and the generic
  // dismiss. Both are primary CTAs: the header carries them on desktop, the
  // bottom bar splits them on mobile.
  const headerAction = PreviewHeaderActions ? (
    <PreviewHeaderActions taskId={notificationId} />
  ) : null
  const dismissButton = isTaskPending ? (
    <Button
      variant={dismissAction?.variant ?? 'primary'}
      onClick={handleDone}
      disabled={completeTaskMutation.isPending}
      data-testid="watchtower.notification.doneButton"
    >
      {dismissAction?.label ?? 'Done'}
    </Button>
  ) : null
  const actions =
    headerAction || dismissButton ? (
      <>
        {headerAction}
        {dismissButton}
      </>
    ) : undefined

  return (
    <ErrorBoundary name="NotificationPreview" fallback={<PageError />}>
      <InboxPreview
        header={<PreviewHeaderContent taskId={notificationId} />}
        actions={actions}
      >
        <NodeTimeline data-testid="watchtower.notification.preview">
          <PreviewContent taskId={notificationId} />
        </NodeTimeline>
      </InboxPreview>
    </ErrorBoundary>
  )
}
